// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { LayoutChangeEvent, Modal, Pressable, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Video, ResizeMode } from 'expo-av';
import Markdown from 'react-native-markdown-display';

import { Text, View } from '@/components/Themed';
import GameRulesPicker from '@/components/GameRulesPicker';
import GameWeightControl from '@/components/GameWeightControl';
import GoffyButton from '@/components/GoffyButton';
import {
  getGameDisplayDescription,
  getGameDisplayInfo,
  getRuleSetDisplayName,
} from '@/features/game-definitions/i18n';
import type { GameRuleSet } from '@/features/game-definitions/types';
import { getRuleSetById, sortRuleSets } from '@/features/game-selection/ruleSets';
import { useI18n } from '@/i18n';
import { buildMediaUrl } from '@/services/mediaUrl';
import {
  readVideoAspectRatio,
  resolveDeclaredAspectRatio,
  resolveInfoVideoBox,
} from '@/components/roundGameInfoVideoLayout';

type RoundGameInfo = {
  /** Needed for the i18n lookups. Every call site already passes a whole GameDefinition. */
  id?: string | null;
  name?: string | null;
  info?: string | null;
  description?: string | null;
  competitionType?: string | null;
  defaultWeightPercent?: number | null;
  rules?: GameRuleSet[] | null;
  video?: string | null;
  videoWebWidth?: number | null;
  videoWebHeight?: number | null;
  videoMobileWidth?: number | null;
  videoMobileHeight?: number | null;
};

type RoundGameInfoModalProps = {
  visible: boolean;
  game: RoundGameInfo | null;
  apiBaseUrl: string;
  onClose: () => void;
  onWeightPress?: () => void;
  /** The rule set in force. Null falls back to the definition's own default. */
  selectedRuleSetId?: string | null;
  /** The tour's default, so a round can see what it would be overriding. */
  tourRuleSetId?: string | null;
  /** Omit to show the rules read-only. */
  onSelectRuleSet?: (ruleSetId: string) => void;
  ruleScope?: 'round' | 'tour';
};

const MOBILE_VIDEO_BREAKPOINT = 900;

// The video shares the modal with the description, the game type and sometimes the weight control,
// so a portrait video is capped rather than allowed to fill the card.
const MAX_VIDEO_HEIGHT_FRACTION = 0.45;

export default function RoundGameInfoModal({
  visible,
  game,
  apiBaseUrl,
  onClose,
  onWeightPress,
  selectedRuleSetId,
  tourRuleSetId,
  onSelectRuleSet,
  ruleScope = 'round',
}: RoundGameInfoModalProps) {
  const { t } = useI18n();
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const infoVideoRef = useRef<Video>(null);
  const [videoSlotWidth, setVideoSlotWidth] = useState(0);
  const [measuredAspectRatio, setMeasuredAspectRatio] = useState<number | null>(null);
  const gameName = (game?.name ?? '').trim() || t('selectGames.gameNameFallback');
  const descriptionText = buildGameDescription(game, t('selectGames.infoDescriptionEmpty'), t('selectGames.infoDescriptionExtra'));
  const gameTypeText = (game?.competitionType ?? '').trim() || t('selectGames.infoGameTypeDefault');
  const weightPercent = safeWeight(game?.defaultWeightPercent, 100);
  const showWeightControl = typeof onWeightPress === 'function';

  // One <Modal> with two bodies rather than two stacked modals: no screen in this app nests
  // them, because on iOS the second simultaneously-visible sibling modal never presents.
  const [view, setView] = useState<'info' | 'rules'>('info');
  const ruleSets = useMemo(() => sortRuleSets(game?.rules), [game?.rules]);
  // One rule set is not a choice, so the button would open a picker with nothing to pick.
  const canPickRules = ruleSets.length > 1;
  const activeRuleSetId = getRuleSetById(ruleSets, selectedRuleSetId)?.id
    ?? ruleSets.find((ruleSet) => ruleSet.isDefault === true)?.id
    ?? ruleSets[0]?.id
    ?? null;
  const activeRuleSetName = getRuleSetDisplayName(
    game?.id,
    getRuleSetById(ruleSets, activeRuleSetId),
    activeRuleSetId
  );

  useEffect(() => {
    if (!visible) {
      infoVideoRef.current?.stopAsync().catch(() => {});
      // Reopening always lands on the info view; a stale 'rules' would show the picker straight
      // away for whichever game is opened next.
      setView('info');
    }
  }, [visible]);

  // A new video has its own shape, so the previous measurement must not survive into it.
  useEffect(() => {
    setMeasuredAspectRatio(null);
  }, [game?.video]);

  const infoVideoUri = useMemo(
    () => resolveGameVideoUri(game?.video, apiBaseUrl, viewportWidth),
    [apiBaseUrl, game?.video, viewportWidth]
  );

  // The declared dimensions only carry the box until the player reports the video's real size;
  // definitions that were never re-measured still claim a shape their video does not have.
  const aspectRatio = measuredAspectRatio
    ?? resolveDeclaredAspectRatio(game, viewportWidth > 0 && viewportWidth <= MOBILE_VIDEO_BREAKPOINT);

  const infoVideoBox = useMemo(
    () => resolveInfoVideoBox(aspectRatio, videoSlotWidth, viewportHeight * MAX_VIDEO_HEIGHT_FRACTION),
    [aspectRatio, videoSlotWidth, viewportHeight]
  );

  const handleVideoSlotLayout = (event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;
    if (width > 0 && Math.abs(width - videoSlotWidth) >= 1) {
      setVideoSlotWidth(width);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => (view === 'rules' ? setView('info') : onClose())}
    >
      <View style={styles.infoOverlay}>
        <View style={styles.infoCard}>
          {view === 'rules' ? (
            <GameRulesPicker
              gameId={game?.id}
              gameName={gameName}
              ruleSets={ruleSets}
              selectedRuleSetId={activeRuleSetId}
              tourRuleSetId={tourRuleSetId}
              onSelect={onSelectRuleSet}
              scope={ruleScope}
              onBack={() => setView('info')}
              onClose={onClose}
            />
          ) : (
          <ScrollView
            style={styles.infoScroll}
            contentContainerStyle={styles.infoScrollContent}
            showsVerticalScrollIndicator
          >
            <Text style={styles.infoTitle}>
              {t('selectGames.infoVideoTitleByGame', { name: gameName })}
            </Text>
            {infoVideoUri ? (
              <View
                testID="round-game-info-video-slot"
                style={styles.infoVideoSlot}
                onLayout={handleVideoSlotLayout}
              >
                {infoVideoBox.width > 0 ? (
                  <Video
                    testID="round-game-info-video"
                    ref={infoVideoRef}
                    source={{ uri: infoVideoUri }}
                    style={[
                      styles.infoVideo,
                      { width: infoVideoBox.width, height: infoVideoBox.height },
                    ]}
                    // The box above sizes the picture only if the <video> fills it: on web
                    // expo-av leaves that element at the file's own pixel size, absolutely
                    // positioned, so the box would crop the video instead of scaling it and
                    // CONTAIN would never act.
                    videoStyle={styles.infoVideoSurface}
                    resizeMode={ResizeMode.CONTAIN}
                    useNativeControls
                    shouldPlay={visible}
                    onReadyForDisplay={(event) => {
                      const ratio = readVideoAspectRatio(event);
                      if (ratio !== null) {
                        setMeasuredAspectRatio(ratio);
                      }
                    }}
                  />
                ) : null}
              </View>
            ) : null}
            <Text style={styles.infoDescriptionTitle}>{t('selectGames.infoDescriptionTitle')}</Text>
            <Markdown style={descriptionMarkdownStyles}>{descriptionText}</Markdown>
            <Text style={styles.infoMetaTitle}>{t('selectGames.infoGameTypeTitle')}</Text>
            <Text style={styles.infoMetaText}>{gameTypeText}</Text>
            {ruleSets.length > 0 ? (
              <>
                <Text style={styles.infoMetaTitle}>{t('selectGames.rulesSectionTitle')}</Text>
                <Text style={styles.infoMetaText}>{activeRuleSetName}</Text>
                {canPickRules ? (
                  <GoffyButton
                    testID="game-info-rules-button"
                    label={t('selectGames.rulesButton')}
                    size="compact"
                    onPress={() => setView('rules')}
                    style={styles.infoRulesButton}
                  />
                ) : null}
              </>
            ) : null}
            {showWeightControl ? (
              <>
                <Text style={styles.infoMetaTitle}>{t('selectGames.infoWeightTitle')}</Text>
                <GameWeightControl
                  weightPercent={weightPercent}
                  onPress={onWeightPress}
                  selected
                  centered={false}
                  showLabel
                />
                <Text style={styles.infoMetaText}>{t('selectGames.infoWeightValue', { weight: weightPercent })}</Text>
                <Text style={styles.infoMetaHelp}>{t('selectGames.infoWeightHelp')}</Text>
              </>
            ) : null}
            <Pressable testID="game-info-close-button" style={styles.infoCloseButton} onPress={onClose}>
              <Text style={styles.infoCloseButtonText}>{t('selectGames.closeVideo')}</Text>
            </Pressable>
          </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
}

function resolveGameVideoUri(videoPath: string | null | undefined, apiBaseUrl: string, viewportWidth: number): string | null {
  const raw = (videoPath ?? '').trim();
  if (!raw) {
    return null;
  }

  const preferMobile = viewportWidth > 0 && viewportWidth <= MOBILE_VIDEO_BREAKPOINT;
  const resolvePresetAndKey = (key: string) => {
    const normalizedKey = key.replace(/^\/+/, '');
    if (normalizedKey.startsWith('video/mobile/')) {
      return { key: `video/${normalizedKey.slice('video/mobile/'.length)}`, preset: 'mobile' };
    }

    if (normalizedKey.startsWith('video/web/')) {
      return { key: `video/${normalizedKey.slice('video/web/'.length)}`, preset: 'web' };
    }

    if (!normalizedKey.startsWith('video/')) {
      return { key: normalizedKey, preset: preferMobile ? 'mobile' : 'web' };
    }

    return {
      key: normalizedKey,
      preset: preferMobile ? 'mobile' : 'web',
    };
  };

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  if (raw.startsWith('s3://')) {
    const withoutScheme = raw.slice('s3://'.length);
    const slashIndex = withoutScheme.indexOf('/');
    if (slashIndex >= 0 && slashIndex + 1 < withoutScheme.length) {
      const key = withoutScheme.slice(slashIndex + 1);
      if (key.startsWith('assets-generated/video/')) {
        const normalized = key.slice('assets-generated/'.length);
        const resolved = resolvePresetAndKey(normalized);
        return buildMediaUrl('video', resolved.key, resolved.preset);
      }

      if (key.startsWith('video/')) {
        const resolved = resolvePresetAndKey(key);
        return buildMediaUrl('video', resolved.key, resolved.preset);
      }
    }
    return null;
  }

  if (raw.startsWith('/video/')) {
    const resolved = resolvePresetAndKey(raw);
    return buildMediaUrl('video', resolved.key, resolved.preset);
  }

  if (raw.startsWith('video/')) {
    const resolved = resolvePresetAndKey(raw);
    return buildMediaUrl('video', resolved.key, resolved.preset);
  }

  if (raw.startsWith('/')) {
    return `${apiBaseUrl}${raw}`;
  }

  return `${apiBaseUrl}/${raw}`;
}

function safeWeight(value: number | null | undefined, fallback: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback;
  }

  return Math.max(0, Math.min(100, Math.round(value)));
}

/**
 * Resolved through the same i18n helpers the game tiles use. Reading `description`/`info` raw
 * put untranslated backend text next to a translated title.
 */
function buildGameDescription(game: RoundGameInfo | null, emptyFallback: string, extraText: string) {
  const primary = getGameDisplayDescription(game).trim();
  const secondary = getGameDisplayInfo(game).trim();
  const merged = primary && secondary && primary !== secondary ? `${primary} ${secondary}` : (primary || secondary || emptyFallback);
  return `${merged} ${extraText}`.trim();
}

const styles = StyleSheet.create({
  infoOverlay: {
    flex: 1,
    backgroundColor: 'rgba(4,7,17,0.72)',
    justifyContent: 'center',
    padding: 20,
  },
  infoCard: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '86%',
    alignSelf: 'center',
    backgroundColor: '#07200D',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 12,
  },
  infoScroll: {
    width: '100%',
  },
  infoScrollContent: {
    gap: 12,
    paddingBottom: 2,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#F5F5DC',
    textAlign: 'center',
  },
  // The slot is what onLayout measures; keeping the inset here means the video box is fitted to
  // 82% of the card exactly once, rather than the width being reduced twice as it used to be.
  infoVideoSlot: {
    width: '82%',
    alignSelf: 'center',
  },
  infoVideo: {
    borderRadius: 12,
    alignSelf: 'center',
    // No placeholder colour: the box now has the video's own aspect ratio, so any visible
    // background would mean the fit is wrong rather than filling an intended gap.
    backgroundColor: 'transparent',
  },
  /** Fills the box above; see the `videoStyle` comment at the call site. */
  infoVideoSurface: {
    width: '100%',
    height: '100%',
  },
  infoDescriptionTitle: {
    marginTop: 4,
    color: '#2d6b66',
    fontSize: 12,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  infoMetaTitle: {
    color: '#2d6b66',
    fontSize: 12,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontWeight: '700',
    marginTop: 4,
  },
  infoMetaText: {
    color: '#F5F5DC',
    marginTop: -4,
    lineHeight: 18,
  },
  infoMetaHelp: {
    color: '#C8C8B0',
    marginTop: -4,
    lineHeight: 17,
    fontSize: 13,
  },
  infoRulesButton: {
    alignSelf: 'flex-start',
  },
  infoCloseButton: {
    alignSelf: 'center',
    backgroundColor: '#A1D493',
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 10,
  },
  infoCloseButtonText: {
    color: '#F5F5DC',
    fontWeight: '700',
  },
});

/** Plain object, not StyleSheet.create — react-native-markdown-display merges these into its own
 * default rule styles per node type, and expects raw style objects to merge into. */
const descriptionMarkdownStyles = {
  body: { margin: 0, padding: 0 },
  paragraph: { marginTop: -4, marginBottom: 0 },
  text: { color: '#F5F5DC', lineHeight: 19 },
  strong: { fontWeight: '700' as const },
  bullet_list: { marginTop: 2, marginBottom: 0 },
  ordered_list: { marginTop: 2, marginBottom: 0 },
  list_item: { marginTop: 0, marginBottom: 0 },
  link: { color: '#A1D493', textDecorationLine: 'underline' as const },
};
